import {Error} from '@mui/icons-material';
import {Alert, Box, Checkbox, FormControlLabel, Grid, SelectChangeEvent, Switch, Typography,} from '@mui/material';
import {Form, Formik, FormikConfig, FormikHandlers, FormikProps} from 'formik';
import {ChangeEvent, FC, useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {companyApi} from 'src/api';
import {useBusinessId, useAppDispatch, useAppSelector} from 'src/app/hooks';
import {AppDispatch} from 'src/app/store';
import {AMPFormSelect} from 'src/components/common/AMPFormSelect';
import {AMPInput} from 'src/components/common/AMPInput';
import {CancelButton} from 'src/components/common/CancelButton';
import {SubmitButton} from 'src/components/common/SubmitButton';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {getHelperText} from 'src/utils/forms';
import {isErrorObject, processedLanguages, valuesTrimmer} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {addCompanyInitialValue} from '../formData/common/initialData';
import {actionsWrapperSx, alertSx, formSx} from './CompanyForm.sx';
import {softSwitchSx} from 'src/components/common/switch.sx';
import {BusinessFormTypes} from './CompanyForm.types';
import {systemApi} from '../../../api/system';
import {handleGetCountries} from '../../../hooks/useGetCountries';
import {SelectValue} from '../../../types';
import {TariffType} from '../formData/tariff';
import {allRegionsValue} from '../../../utils/consts';
import {companyFormSchema, CreateCompanyFormType} from '../formData/company';
import {AMPPhoneNumber} from 'src/components/common/AMPPhoneNumber';

type AddNewCompanyMutation = ReturnType<typeof companyApi.useAddNewCompanyMutation>[0];

const handleSubmit =
    (
        trigger: AddNewCompanyMutation,
        dispatch: AppDispatch,
        handleClose: () => void
    ): FormikConfig<CreateCompanyFormType>['onSubmit'] =>
        async (value) => {
            try {
                dispatch(preloaderChangeStatus(true));
                const values = valuesTrimmer(value);

                const {
                    businessId,
                    companyName,
                    billingEmail,
                    language,
                    loginEmail,
                    billingAddressCity,
                    billingAddressCountry,
                    billingAddressHouseNumber,
                    billingAddressParkingPlace,
                    billingAddressRegion,
                    billingAddressStreet,
                    billingAddressZipCode,
                    contactPersonFirstName,
                    contactPersonLastName,
                    contactPersonPhone,
                    parkingAddressCity,
                    parkingAddressCountry,
                    parkingAddressHouseNumber,
                    parkingAddressParkingPlace,
                    parkingAddressRegion,
                    parkingAddressStreet,
                    parkingAddressZipCode,
                    isParkingAddressSameAsBillingAddress,
                    chargeConsumption,
                } = values;

                const input = {
                    businessId,
                    billingEmail: billingEmail || undefined,
                    chargeConsumption,
                    companyName,
                    language,
                    loginEmail,
                    billingAddress: {
                        city: billingAddressCity,
                        country: billingAddressCountry,
                        houseNumber: billingAddressHouseNumber,
                        parkingPlace: billingAddressParkingPlace,
                        region: billingAddressRegion,
                        street: billingAddressStreet,
                        zipCode: billingAddressZipCode,
                    },
                    contactPerson: {
                        firstName: contactPersonFirstName,
                        lastName: contactPersonLastName,
                        phone: contactPersonPhone,
                    },
                    parkingAddress: {
                        city: isParkingAddressSameAsBillingAddress ? billingAddressCity : parkingAddressCity,
                        country: isParkingAddressSameAsBillingAddress
                            ? billingAddressCountry
                            : parkingAddressCountry,
                        houseNumber: isParkingAddressSameAsBillingAddress
                            ? billingAddressHouseNumber
                            : parkingAddressHouseNumber,
                        parkingPlace: isParkingAddressSameAsBillingAddress
                            ? billingAddressParkingPlace
                            : parkingAddressParkingPlace,
                        region: isParkingAddressSameAsBillingAddress
                            ? billingAddressRegion
                            : parkingAddressRegion,
                        street: isParkingAddressSameAsBillingAddress
                            ? billingAddressStreet
                            : parkingAddressStreet,
                        zipCode: isParkingAddressSameAsBillingAddress
                            ? billingAddressZipCode
                            : parkingAddressZipCode,
                    },
                };

                await trigger(input).unwrap();
                handleClose();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const CompanyForm: FC<BusinessFormTypes> = ({handleClose}) => {
    const businessId = useBusinessId();
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const [trigger] = companyApi.useAddNewCompanyMutation();
    const {lang} = useAppSelector((state) => state.common);

    const [getCountries, countries] = systemApi.useLazyGetCountriesQuery();
    const [activeCountry, setActiveCountry] = useState<string>('');

    const processedRegions = useMemo(() => {
        const initialArray: SelectValue[] = [
            {
                id: 0,
                name: activeCountry.length ? intl.formatMessage({id: activeCountry.toLowerCase()}) : '',
                value: allRegionsValue,
            },
        ];
        if (activeCountry.length) {
            const mappedRegions: SelectValue[] | undefined = countries.data
                ?.find((country) => country.name === activeCountry)
                ?.regions?.map((region) => ({
                    id: region.id,
                    name: intl.formatMessage({id: region.name}),
                    value: region.name,
                }));
            return mappedRegions?.concat(initialArray[0]) ?? initialArray;
        }
        return initialArray;
    }, [activeCountry, lang]);

    const processedCountries =
        countries?.data?.map((country) => ({
            id: country.id,
            name: intl.formatMessage({id: country.name.toLowerCase()}),
            value: country.name,
        })) ?? [];

    const handleChangeCountry =
        (
            handleChange: FormikHandlers['handleChange'],
            setFieldValue: FormikProps<TariffType>['setFieldValue']
        ) =>
            (event: SelectChangeEvent) => {
                setActiveCountry(event.target.value);
                setFieldValue('region', '');
                return handleChange(event);
            };

    const handleChangePhone =
        (setFieldValue: FormikProps<TariffType>['setFieldValue'], fieldName: string) =>
            (newValue: string | number | null | undefined) => {
                if (newValue) {
                    setFieldValue(fieldName, `+${newValue}`);
                }
            };

    const toggleAddressBlock =
        (
            handleChange: FormikHandlers['handleChange'],
            setFieldValue: FormikProps<CreateCompanyFormType>['setFieldValue'],
            values: CreateCompanyFormType
        ) =>
            (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
                handleChange(event);

                if (checked) {
                    setFieldValue('parkingAddressCity', values.billingAddressCity);
                    setFieldValue('parkingAddressCountry', values.billingAddressCountry);
                    setFieldValue('parkingAddressHouseNumber', values.billingAddressHouseNumber);
                    setFieldValue('parkingAddressParkingPlace', values.billingAddressParkingPlace);
                    setFieldValue('parkingAddressRegion', values.billingAddressRegion);
                    setFieldValue('parkingAddressStreet', values.billingAddressStreet);
                    setFieldValue('parkingAddressZipCode', values.billingAddressZipCode);

                    return;
                }

                setFieldValue('parkingAddressCity', '');
                setFieldValue('parkingAddressCountry', '');
                setFieldValue('parkingAddressHouseNumber', '');
                setFieldValue('parkingAddressParkingPlace', '');
                setFieldValue('parkingAddressRegion', '');
                setFieldValue('parkingAddressStreet', '');
                setFieldValue('parkingAddressZipCode', '');
            };

    useEffect(() => {
        void handleGetCountries(getCountries, dispatch);
    }, []);

    return (
        <Formik
            // businessId in the defaults is a hardcoded 1. Harmless for a business user, whose
            // own business the server forces onto the body anyway, and wrong for the one caller
            // it does not force: every company an administrator created landed in AMP IT.
            initialValues={{...addCompanyInitialValue, businessId}}
            validationSchema={companyFormSchema}
            onSubmit={handleSubmit(trigger, dispatch, handleClose)}
        >
            {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                  setFieldValue,
                  touched,
                  values,
              }: FormikProps<CreateCompanyFormType>) => (
                <Form
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <Box sx={formSx}>
                        <Box>
                            <Typography
                                variant="subtitle1"
                                color="textPrimary"
                            >
                                <FormattedMessage id="companyDetails"/>
                            </Typography>

                            <Grid
                                container
                                spacing={2}
                            >
                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* Company name */}
                                    <AMPInput
                                        value={values.companyName}
                                        type={'name'}
                                        label={'companyName'}
                                        input={'companyName'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched.companyName && Boolean(errors.companyName)}
                                        helperText={getHelperText('companyName', touched, errors, intl)}
                                    />
                                </Grid>

                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* Login email */}
                                    <AMPInput
                                        value={values.loginEmail}
                                        type={'emailAddress'}
                                        label={'loginEmail'}
                                        input={'loginEmail'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched.loginEmail && Boolean(errors.loginEmail)}
                                        helperText={getHelperText('loginEmail', touched, errors, intl)}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Box>
                            <Typography
                                variant="subtitle1"
                                color="textPrimary"
                            >
                                <FormattedMessage id="billingAddress"/>
                            </Typography>

                            <Grid
                                container
                                spacing={2}
                            >
                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* Country */}
                                    <AMPFormSelect
                                        data={processedCountries}
                                        value={values.billingAddressCountry}
                                        label={'country'}
                                        input={'billingAddressCountry'}
                                        onChange={handleChangeCountry(handleChange, setFieldValue)}
                                        onBlur={handleBlur}
                                        hasError={
                                            touched.billingAddressCountry && Boolean(errors.billingAddressCountry)
                                        }
                                        helperText={getHelperText('country', touched, errors, intl)}
                                    />
                                </Grid>

                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* Region */}
                                    <AMPFormSelect
                                        data={processedRegions}
                                        value={values.billingAddressRegion}
                                        label={'region'}
                                        input={'billingAddressRegion'}
                                        disabled={!activeCountry.length}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched.billingAddressRegion && Boolean(errors.billingAddressRegion)}
                                        helperText={getHelperText('region', touched, errors, intl)}
                                    />
                                </Grid>

                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* City */}
                                    <AMPInput
                                        type="text"
                                        value={values.billingAddressCity}
                                        label={'city'}
                                        input={'billingAddressCity'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched.billingAddressCity && Boolean(errors.billingAddressCity)}
                                        helperText={getHelperText('billingAddressCity', touched, errors, intl)}
                                    />
                                </Grid>

                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* Street */}
                                    <AMPInput
                                        value={values.billingAddressStreet}
                                        type={'street'}
                                        label={'street'}
                                        input={'billingAddressStreet'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched.billingAddressStreet && Boolean(errors.billingAddressStreet)}
                                        helperText={getHelperText('billingAddressStreet', touched, errors, intl)}
                                    />
                                </Grid>

                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* Building № */}
                                    <AMPInput
                                        value={values.billingAddressHouseNumber}
                                        type={'text'}
                                        label={'houseNumber'}
                                        input={'billingAddressHouseNumber'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={
                                            touched.billingAddressHouseNumber && Boolean(errors.billingAddressHouseNumber)
                                        }
                                        helperText={getHelperText('billingAddressHouseNumber', touched, errors, intl)}
                                    />
                                </Grid>

                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* ZIP code */}
                                    <AMPInput
                                        value={values.billingAddressZipCode}
                                        type={'number'}
                                        label={'zipCode'}
                                        input={'billingAddressZipCode'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={
                                            touched.billingAddressZipCode && Boolean(errors.billingAddressZipCode)
                                        }
                                        helperText={getHelperText('billingAddressZipCode', touched, errors, intl)}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Box>
                            <Typography
                                variant="subtitle1"
                                color="textPrimary"
                            >
                                <FormattedMessage id="contactPerson"/>
                            </Typography>

                            <Grid
                                container
                                spacing={2}
                            >
                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* First name */}
                                    <AMPInput
                                        value={values.contactPersonFirstName}
                                        type={'firstName'}
                                        label={'firstName'}
                                        input={'contactPersonFirstName'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={
                                            touched.contactPersonFirstName && Boolean(errors.contactPersonFirstName)
                                        }
                                        helperText={getHelperText('contactPersonFirstName', touched, errors, intl)}
                                    />
                                </Grid>

                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* Last name */}
                                    <AMPInput
                                        value={values.contactPersonLastName}
                                        type={'lastName'}
                                        label={'contactPersonLastName'}
                                        input={'contactPersonLastName'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={
                                            touched.contactPersonLastName && Boolean(errors.contactPersonLastName)
                                        }
                                        helperText={getHelperText('contactPersonLastName', touched, errors, intl)}
                                    />
                                </Grid>


                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* Phone number */}
                                    <AMPPhoneNumber
                                        value={values.contactPersonPhone}
                                        onChange={handleChangePhone(setFieldValue, 'contactPersonPhone')}
                                        label={'contactPersonPhone'}
                                        onBlur={handleBlur}
                                        hasError={touched.contactPersonPhone && Boolean(errors.contactPersonPhone)}
                                        helperText={getHelperText('contactPersonPhone', touched, errors, intl)}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Box>
                            <Typography
                                variant="subtitle1"
                                color="textPrimary"
                            >
                                <FormattedMessage id="parkingAddress"/>
                            </Typography>
                            <FormControlLabel
                                label={intl.formatMessage({id: 'useBillingAddress'})}
                                control={
                                    <Checkbox
                                        color="secondary"
                                        name="isParkingAddressSameAsBillingAddress"
                                        value={values.isParkingAddressSameAsBillingAddress}
                                        onChange={(event: ChangeEvent<HTMLInputElement>, checked: boolean) =>
                                            toggleAddressBlock(handleChange, setFieldValue, values)(event, checked)
                                        }
                                    />
                                }
                            />

                            <Grid
                                container
                                spacing={2}
                            >
                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* Country */}
                                    <AMPFormSelect
                                        data={processedCountries}
                                        value={values.parkingAddressCountry}
                                        label={'country'}
                                        input={'parkingAddressCountry'}
                                        onChange={handleChangeCountry(handleChange, setFieldValue)}
                                        onBlur={handleBlur}
                                        hasError={
                                            touched.parkingAddressCountry && Boolean(errors.parkingAddressCountry)
                                        }
                                        helperText={getHelperText('parkingAddressCountry', touched, errors, intl)}
                                    />
                                </Grid>

                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* Region */}
                                    <AMPFormSelect
                                        data={processedRegions}
                                        value={values.parkingAddressRegion}
                                        label={'region'}
                                        input={'parkingAddressRegion'}
                                        disabled={!activeCountry.length}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched.parkingAddressRegion && Boolean(errors.parkingAddressRegion)}
                                        helperText={getHelperText('parkingAddressRegion', touched, errors, intl)}
                                    />
                                </Grid>

                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* City */}
                                    <AMPInput
                                        type="text"
                                        value={values.parkingAddressCity}
                                        label={'city'}
                                        input={'parkingAddressCity'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched.parkingAddressCity && Boolean(errors.parkingAddressCity)}
                                        helperText={getHelperText('parkingAddressCity', touched, errors, intl)}
                                    />
                                </Grid>

                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* Street */}
                                    <AMPInput
                                        value={values.parkingAddressStreet}
                                        type={'street'}
                                        label={'street'}
                                        input={'parkingAddressStreet'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched.parkingAddressStreet && Boolean(errors.parkingAddressStreet)}
                                        helperText={getHelperText('parkingAddressStreet', touched, errors, intl)}
                                    />
                                </Grid>

                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* Building № */}
                                    <AMPInput
                                        value={values.parkingAddressHouseNumber}
                                        type={'text'}
                                        label={'houseNumber'}
                                        input={'parkingAddressHouseNumber'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={
                                            touched.parkingAddressHouseNumber && Boolean(errors.parkingAddressHouseNumber)
                                        }
                                        helperText={getHelperText('parkingAddressHouseNumber', touched, errors, intl)}
                                    />
                                </Grid>

                                <Grid
                                    lg={6}
                                    xs={12}
                                    item
                                >
                                    {/* ZIP code */}
                                    <AMPInput
                                        value={values.parkingAddressZipCode}
                                        type={'number'}
                                        label={'zipCode'}
                                        input={'parkingAddressZipCode'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={
                                            touched.parkingAddressZipCode && Boolean(errors.parkingAddressZipCode)
                                        }
                                        helperText={getHelperText('parkingAddressZipCode', touched, errors, intl)}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Box>
                            <Typography
                                variant="subtitle1"
                                color="textPrimary"
                            >
                                <FormattedMessage id="additionalFields"/>
                            </Typography>

                            <Grid
                                container
                                spacing={2}
                            >
                                <Grid
                                    lg={12}
                                    xs={12}
                                    item
                                >
                                    {/* Billing email */}
                                    <AMPInput
                                        value={values.billingEmail || ''}
                                        type={'emailAddress'}
                                        label={'billingEmail'}
                                        input={'billingEmail'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched.billingEmail && Boolean(errors.billingEmail)}
                                        helperText={getHelperText('billingEmail', touched, errors, intl)}
                                    />
                                </Grid>

                                <Grid
                                    lg={12}
                                    xs={12}
                                    item
                                >
                                    <Alert
                                        sx={alertSx}
                                        severity="info"
                                        icon={<Error/>}
                                    >
                                        <ul>
                                            <li>
                                                <Typography
                                                    variant="caption"
                                                    color="textPrimary"
                                                >
                                                    <FormattedMessage id="additionalFieldsExplanationFirst"/>
                                                </Typography>
                                            </li>
                                            <li>
                                                <Typography
                                                    variant="caption"
                                                    color="textPrimary"
                                                >
                                                    <FormattedMessage id="additionalFieldsExplanationSecond"/>
                                                </Typography>
                                            </li>
                                            <li>
                                                <Typography
                                                    variant="caption"
                                                    color="textPrimary"
                                                >
                                                    <FormattedMessage id="additionalFieldsExplanationThird"/>
                                                </Typography>
                                            </li>
                                        </ul>
                                    </Alert>
                                </Grid>

                                <Grid
                                    lg={12}
                                    xs={12}
                                    item
                                >
                                    {/* Language */}
                                    <AMPFormSelect
                                        data={processedLanguages(intl)}
                                        value={values.language}
                                        label={'language'}
                                        input={'language'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched.language && Boolean(errors.language)}
                                        helperText={getHelperText('language', touched, errors, intl)}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Box>
                            <Typography
                                variant="subtitle1"
                                color="textPrimary"
                            >
                                <FormattedMessage id="invoiceSettings"/>
                            </Typography>

                            <FormControlLabel
                                label={intl.formatMessage({id: 'chargeConsumption'})}
                                control={
                                    <Switch
                                        sx={softSwitchSx}
                                        name="chargeConsumption"
                                        checked={values.chargeConsumption}
                                        onChange={handleChange}
                                    />
                                }
                            />
                        </Box>
                    </Box>

                    <Box sx={actionsWrapperSx}>
                        <CancelButton
                            text="cancel"
                            handler={handleClose}
                        />
                        <SubmitButton
                            text={'createUser'}
                            color="primary"
                            isSubmitting={isSubmitting}
                        />
                    </Box>
                </Form>
            )}
        </Formik>
    );
};
